import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { z } from 'zod';
import { AuthLayout } from '@/components/shell/auth-layout';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { FormAlert, PasswordField, passwordStrength, TextField } from '@/features/auth/form-parts';
import { authClient, authErrorMessage } from '@/lib/auth-client';
import { QueryKeys } from '@/lib/queries';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_auth/signup')({
  component: SignUpScreen,
});

const zSignUp = z.object({
  name: z.string().trim().min(1, 'Enter a display name').max(60, 'Keep it under 60 characters'),
  email: z.email('Enter a valid email address'),
  password: z.string().min(10, 'Use at least 10 characters').max(128, 'Use at most 128 characters'),
});
type FieldErrors = Partial<Record<keyof z.infer<typeof zSignUp>, string>>;

function SignUpScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const strength = passwordStrength(form.password);

  const update = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setFieldErrors((errs) => ({ ...errs, [key]: undefined }));
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const parsed = zSignUp.safeParse(form);
    if (!parsed.success) {
      const next: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof FieldErrors;
        next[key] ??= issue.message;
      }
      setFieldErrors(next);
      return;
    }
    setPending(true);
    const { data, error: signUpError } = await authClient.signUp.email({
      name: parsed.data.name,
      email: parsed.data.email,
      password: parsed.data.password,
      callbackURL: '/',
    });
    setPending(false);
    if (signUpError) {
      setError(authErrorMessage(signUpError));
      return;
    }
    // With email verification off, sign-up returns a live session; otherwise the user confirms first.
    if (data?.token) {
      await queryClient.invalidateQueries({ queryKey: QueryKeys.me });
      await navigate({ to: '/' });
      return;
    }
    await navigate({ to: '/check-email', search: { email: parsed.data.email } });
  };

  return (
    <AuthLayout
      title="Create your account"
      footer={
        <>
          Already have an account?{' '}
          <Link to="/login" className="link">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <FormAlert message={error} />
        <TextField
          label="Display name"
          autoComplete="nickname"
          value={form.name}
          onChange={update('name')}
          error={fieldErrors.name ?? null}
        />
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          inputMode="email"
          value={form.email}
          onChange={update('email')}
          error={fieldErrors.email ?? null}
        />
        <div className="flex flex-col gap-1.5">
          <PasswordField
            label="Password"
            autoComplete="new-password"
            value={form.password}
            onChange={update('password')}
            error={fieldErrors.password ?? null}
          />
          {form.password.length > 0 && !fieldErrors.password && (
            <div className="flex items-center gap-3" aria-live="polite">
              <div className="flex w-24 shrink-0 gap-1" aria-hidden="true">
                {[1, 2, 3].map((step) => (
                  <span
                    key={step}
                    className={cn(
                      'h-1 flex-1 rounded-full bg-surface-3',
                      strength.score >= step &&
                        (strength.score === 1 ? 'bg-loss' : strength.score === 2 ? 'bg-amber' : 'bg-gain'),
                    )}
                  />
                ))}
              </div>
              <span className="text-[0.75rem] text-muted-foreground">{strength.label}</span>
            </div>
          )}
        </div>
        <Button type="submit" disabled={pending} className="mt-1 h-9 w-full">
          {pending && <Spinner />}
          Create account
        </Button>
      </form>
    </AuthLayout>
  );
}
